import sortBy from 'lodash/sortBy';
import groupBy from 'lodash/groupBy';

import type {
  OverlayManagerState,
  OverlayManagerAction,
  Overlay,
  ComponentName,
  OverlayRef,
} from './types';
import { stackingOrder } from './overlay-manager.constants';
import type { IndexName, OverlayRect } from './overlay-manager.constants';
import {
  VisibilityStates,
  VisibilityState,
} from '../../content/pop-out-media-manager/types';

const sortOverlaysByIndex = (overlays: Array<Overlay>): Array<Overlay> =>
  sortBy(overlays, [
    overlay => stackingOrder[overlay.indexName],
    overlay => overlay.clientRect?.height,
  ]);

const componentExists = (
  overlays: Array<Overlay>,
  componentName: string,
): boolean =>
  overlays.findIndex(overlay => overlay.componentName === componentName) !== -1;

const getDimensionsWithMargin = (ref: OverlayRef): OverlayRect | null => {
  if (!ref.current) {
    return null;
  }

  const baseRect = ref.current.getBoundingClientRect();
  const { height } = baseRect;
  const { marginTop, marginBottom } = getComputedStyle(ref.current);

  const fullHeight = height + parseInt(marginTop) + parseInt(marginBottom);

  return {
    ...baseRect,
    height: fullHeight,
  };
};

const addOverlay = (
  state: OverlayManagerState,
  componentName: string,
  indexName: IndexName,
  ref: OverlayRef,
): OverlayManagerState => {
  if (componentExists(state.overlays, componentName) || !ref.current) {
    return state;
  }

  return {
    ...state,
    overlays: sortOverlaysByIndex([
      ...state.overlays,
      {
        componentName,
        indexName,
        ref,
        clientRect: getDimensionsWithMargin(ref),
        visibility: VisibilityStates.Hidden,
      },
    ]),
  };
};

const removeOverlay = (
  state: OverlayManagerState,
  componentName: string,
): OverlayManagerState => {
  if (!componentExists(state.overlays, componentName)) {
    return state;
  }

  // splice changes the array in place so we create a new array to delete from.
  const newOverlayList = [...state.overlays];
  const currentIndex = newOverlayList.findIndex(
    overlay => overlay.componentName === componentName,
  );
  newOverlayList.splice(currentIndex, 1);

  return {
    ...state,
    overlays: newOverlayList,
  };
};

const updateOverlayDimensions = (
  state: OverlayManagerState,
  componentName: string,
): OverlayManagerState => {
  return {
    ...state,
    overlays: state.overlays.map(overlay => {
      if (overlay.componentName !== componentName || !overlay.ref.current) {
        return overlay;
      }

      return {
        ...overlay,
        clientRect: getDimensionsWithMargin(overlay.ref),
      };
    }),
  };
};

const updateOverlayVisibility = (
  state: OverlayManagerState,
  componentName: string,
  visibility: VisibilityState,
): OverlayManagerState => {
  return {
    ...state,
    overlays: state.overlays.map(overlay => {
      if (overlay.componentName !== componentName) {
        return overlay;
      }

      return {
        ...overlay,
        clientRect: getDimensionsWithMargin(overlay.ref),
        visibility,
      };
    }),
  };
};

export const overlayManagerReducer = (
  state: OverlayManagerState,
  action: OverlayManagerAction,
): OverlayManagerState => {
  switch (action.type) {
    case 'ADD_OVERLAY':
      return addOverlay(
        state,
        action.payload.componentName,
        action.payload.indexName,
        action.payload.ref,
      );
    case 'REMOVE_OVERLAY':
      return removeOverlay(state, action.payload.componentName);
    case 'UPDATE_OVERLAY_DIMENSIONS':
      return updateOverlayDimensions(state, action.payload.componentName);
    case 'UPDATE_OVERLAY_VISIBILITY':
      return updateOverlayVisibility(
        state,
        action.payload.componentName,
        action.payload.visibility,
      );
  }
  return state;
};

export const calculateInset = (
  componentName: ComponentName | null,
  overlayStack: Array<Overlay>,
): number | null => {
  let overlaysUnderneathComponent = overlayStack;

  let overlayIndex: number | null = null;

  if (componentName !== null) {
    overlayIndex = overlayStack.findIndex(
      overlay => overlay.componentName === componentName,
    );

    if (overlayIndex === -1) {
      return null;
    }

    overlaysUnderneathComponent = overlayStack.slice(0, overlayIndex);
  }

  const groupedByIndex = groupBy(
    overlaysUnderneathComponent,
    overlay => stackingOrder[overlay.indexName],
  );

  return Object.keys(groupedByIndex).reduce<number>((inset, indexValue) => {
    const componentsAtIndex = groupedByIndex[indexValue];

    if (!componentsAtIndex) {
      return inset;
    }

    let overlay = componentsAtIndex[0];
    // overlays are sorted by index and then height ascending, so the largest
    // item is at the last index
    if (componentsAtIndex.length > 1) {
      overlay = componentsAtIndex[componentsAtIndex.length - 1];
    }

    const isHidden = overlay?.visibility === VisibilityStates.Hidden;
    const isAtSameIndex =
      overlayIndex !== null &&
      overlay?.indexName === overlayStack[overlayIndex]?.indexName;

    if (isHidden || isAtSameIndex || !overlay?.clientRect) {
      return inset;
    }

    return inset + overlay?.clientRect.height;
  }, 0);
};

export const getTransformForSide = (inset: number): string => {
  return `translateY(-${inset}px)`;
};
